import { z } from "zod";
import { localeSchema, flagSchema, hardStopSchema } from "./common";

// Form templates are defined in code (typed Zod schemas) and reference i18n keys
// for all user-facing strings. Submissions are stored as JSONB in the DB along
// with the template key + version so historical answers stay interpretable when
// the template evolves.

export const formFieldKindSchema = z.enum([
  "numeric",
  "boolean",
  "single_select",
  "multi_select",
  "short_text",
  "long_text",
  "scale_1_5",
  "date",
  // CAR-53: a repeatable medications list — { name, doseAndFrequency? } rows.
  // Rendered on mobile by MedicationsListField; the schema deriver maps it to
  // a bounded array of objects. No `options` / `numeric`.
  "medications_list",
  // F-07: a document the patient attaches (prescription evidence, older lab
  // results).
  // The ANSWER is a list of FormUpload ids — never the bytes, which live
  // encrypted in their own row. Configured via TemplateField.upload
  // (allowed mime types / max bytes / max count); see intake-template.ts.
  "file_upload",
  // Simon 2026-09-21, on Zeinab's QA walkthrough of F-02 (2026-09-19: "Didn't
  // we say you could only tick yes, with no 'no' option?"): the consents are
  // required CHECKBOXES.
  //
  // A consent is not a two-valued question but an affirmation that must be
  // given. Its answer set is exactly `true` or absent — `false` is not a
  // recorded refusal, it is not an answer at all. TES-792 had already made a
  // declared attestation reject `false` server-side, so the Nej pill the
  // renderer drew was a button that could only ever end in a 400: a choice
  // the interface offered and then punished.
  //
  // WHY A KIND AND NOT A FLAG ON `boolean`. A flag would be one more optional
  // property on TemplateField that every renderer, deriver and summariser is
  // free to ignore, and the next one written would ignore it silently. A new
  // enum member cannot be ignored: every exhaustive `switch` over
  // FormFieldKind becomes a compile error until it has been handled, in the
  // app's FieldRenderer, the API's schema deriver and the pre-consult
  // summary alike. For the one answer format the system is not allowed to get
  // subtly wrong, "the compiler stops you until you have decided" is the
  // property worth buying.
  //
  // `boolean` deliberately keeps its old meaning. The legacy intake seeds use
  // it for real yes/no questions ("Ammar du?"), and the key list in
  // attestation.ts stays load-bearing for the template versions authored
  // before this kind existed — including the one published on QA and prod.
  "consent",
]);
export type FormFieldKind = z.infer<typeof formFieldKindSchema>;

export const formFieldUiHintSchema = z.object({
  field: z.string(),
  kind: formFieldKindSchema,
  labelKey: z.string(),
  helpKey: z.string().optional(),
  unit: z.string().optional(),
});
export type FormFieldUiHint = z.infer<typeof formFieldUiHintSchema>;

export const formTemplateMetaSchema = z.object({
  key: z.string(),
  version: z.number().int().positive(),
  titleKey: z.string(),
  descriptionKey: z.string(),
});
export type FormTemplateMeta = z.infer<typeof formTemplateMetaSchema>;

// Wire shape — dates are ISO strings because this crosses the JSON
// serialization boundary. The admin + mobile clients import this and
// the API's route handlers return values that satisfy it. Domain code
// inside the API still works with Date objects via Prisma; the
// serializer (NestJS) converts on the way out.
export const formSubmissionSchema = z.object({
  id: z.string().uuid(),
  patientId: z.string().uuid().optional(),
  templateKey: z.string(),
  templateVersion: z.number().int().positive(),
  answers: z.record(z.unknown()),
  flags: z.array(flagSchema),
  hardStops: z.array(hardStopSchema),
  prescriptionCycleId: z.string().uuid().nullable().optional(),
  submittedAt: z.string(),
  submittedInLocale: localeSchema,
  reviewedByDoctorId: z.string().uuid().nullable(),
  reviewedAt: z.string().nullable(),
});
export type FormSubmission = z.infer<typeof formSubmissionSchema>;
